/**
 * Greenwax Delivery Widget
 * Инжектируется в корзину Tilda и заменяет стандартный модуль доставки
 */

import { DeliveryWidget } from './ui/DeliveryWidget';
import { ApiClient } from './api/client';
import type { WidgetConfig, DeliveryOption, PickupPoint, DeliveryOrderPayload } from './types';
import { LS_ORDER_BUFFER_KEY } from './types';

// Yandex Maps API key
const YANDEX_MAPS_API_KEY = '5b48f1e1-6bf8-4d8e-bd08-f4ea27c74269';

// Flag for Yandex Maps loading state
let yandexMapsLoading = false;
let yandexMapsLoaded = false;
let yandexMapsCallbacks: Array<() => void> = [];

// Widget instance
let widgetInstance: DeliveryWidget | null = null;

// API client for order submission
let apiClient: ApiClient | null = null;

// Flag to prevent double-submission
let orderSubmitted = false;

// Cached cart data from cart-api
let cachedCartData: {
  cartAmount: number;
  itemsCount: number;
  products: Array<{
    sku: string;
    name: string;
    price: number;
    quantity: number;
    weight: number;      // grams
    length: number;      // cm (converted from mm)
    width: number;       // cm (converted from mm)
    height: number;      // cm (converted from mm)
  }>;
} | null = null;

// For debounced cart change detection
let lastCartHash = '';
let refreshTimeout: ReturnType<typeof setTimeout> | null = null;
let refreshFromTcartTimeout: ReturnType<typeof setTimeout> | null = null;

// MutationObserver for watching Vienna text
let deliveryTextObserver: MutationObserver | null = null;

// Selectors for Tilda cart/checkout
const SELECTORS = {
  // Cart popup
  CART_POPUP: '.t-popup.t-popup_show .t706__cartwin',
  CART_FORM: '.t706__form',

  // Checkout form
  CHECKOUT_FORM: '.t-form__inputsbox',

  // Original delivery blocks to hide
  DELIVERY_ORIGINAL: [
    '.t706__delivery-wrapper',
    '.t706__form-delivery',
    '.t-form__delivery',
    '[data-delivery-services]',
  ].join(', '),

  // Our widget container
  WIDGET_ID: 'gw-delivery-widget',
};

/**
 * Initialize the delivery widget
 */
function init(config: Partial<WidgetConfig> = {}): void {
  const fullConfig: WidgetConfig = {
    apiUrl: config.apiUrl || 'https://api.delivery.greenwax.ru/api/v1',
    debug: config.debug || false,
    onSelect: config.onSelect,
  };

  if (fullConfig.debug) {
    console.log('[GW Delivery] Initializing with config:', fullConfig);
  }

  // Setup cart-api interceptor first (before any cart requests)
  setupCartApiInterceptor();

  // Hook Tilda cart functions for quantity changes
  hookTildaCartFunctions();

  // Setup fallback listeners for quantity changes
  setupCartQuantityListeners();

  // Initialize API client for order submission
  apiClient = new ApiClient(fullConfig.apiUrl);

  // Form submit interception disabled — orders now go via Tilda webhook
  // setupFormSubmitInterceptor();

  // Set tcart.delivery before form submission so Tilda redirects to payment
  setupDeliveryForSubmit();

  // Flush any pending orders from previous failed submissions
  apiClient.flushPendingOrders().catch(err => {
    console.warn('[GW Delivery] Failed to flush pending orders:', err);
  });

  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setupInjector(fullConfig));
  } else {
    setupInjector(fullConfig);
  }
}

/**
 * Intercept fetch to capture cart-api responses
 * This gives us accurate product data including weight and dimensions
 */
function setupCartApiInterceptor(): void {
  const originalFetch = window.fetch;

  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    const url = args[0]?.toString() || '';

    // Check if this is a cart-api request
    if (url.includes('cart-api') || url.includes('delivery.tildacdn.com')) {
      try {
        const clone = response.clone();
        const data = await clone.json();

        if (data.products && Array.isArray(data.products)) {
          // Create hash to detect changes
          const newHash = JSON.stringify({
            amount: data.cartAmount,
            count: data.itemsCount,
            skus: data.products.map((p: any) => `${p.sku}:${p.quantity}`).sort()
          });

          // Only update if cart actually changed
          const cartChanged = newHash !== lastCartHash;
          lastCartHash = newHash;

          cachedCartData = {
            cartAmount: parseFloat(data.cartAmount) || 0,
            itemsCount: parseInt(data.itemsCount, 10) || 0,
            products: data.products.map((p: any) => ({
              sku: p.sku || '',
              name: p.name || '',
              price: parseFloat(p.price) || 0,
              quantity: parseInt(p.quantity, 10) || 1,
              // pack_m in Tilda is already in GRAMS
              weight: parseFloat(p.pack_m || '0'),
              // Dimensions in Tilda are in MILLIMETERS, convert to cm for CDEK
              length: Math.ceil(parseFloat(p.pack_x || '0') / 10) || 0,
              width: Math.ceil(parseFloat(p.pack_y || '0') / 10) || 0,
              height: Math.ceil(parseFloat(p.pack_z || '0') / 10) || 0,
            })),
          };

          console.log('[GW Delivery] Cart data captured:', {
            changed: cartChanged,
            products: cachedCartData.products.map(p => ({
              sku: p.sku,
              qty: p.quantity,
              weight: p.weight + 'g',
              dims: `${p.length}x${p.width}x${p.height}cm`
            }))
          });

          // Expose calculated cart data globally for DeliveryWidget
          const cartData = getCartData();
          (window as any).__gwCachedCartData = {
            products: cartData.items,
            totalPrice: cartData.totalPrice,
            totalWeight: cartData.totalWeight,
            dimensions: cartData.dimensions,
          };

          // Debounced refresh if cart changed
          if (cartChanged && widgetInstance) {
            if (refreshTimeout) {
              clearTimeout(refreshTimeout);
            }
            refreshTimeout = setTimeout(() => {
              console.log('[GW Delivery] Cart changed, refreshing...');
              widgetInstance?.refresh();
            }, 300);
          }
        }
      } catch (e) {
        // Silently ignore JSON parse errors
      }
    }

    return response;
  };

  console.log('[GW Delivery] Cart-api interceptor installed');
}

/**
 * Set tcart.delivery right before Tilda processes form submission.
 *
 * Tilda checks tcart.delivery to know if delivery is configured.
 * If it's not set, Tilda treats the form as a simple contact form
 * and shows "Данные успешно отправлены" instead of redirecting to payment.
 *
 * We can't set tcart.delivery during delivery option selection because
 * Tilda watches it and triggers auto-redirect to payment immediately.
 * Instead, we set it at the moment the submit button is clicked.
 */
function setupDeliveryForSubmit(): void {
  // Use click handler on capture phase — fires before Tilda's own handlers
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const submitBtn = target.closest('.t706__submit, .t-submit, .t706__cartwin-checkout-btn');
    if (!submitBtn) return;

    // Only act if we're in the order form (not the initial cart view)
    const form = document.querySelector('.t706 .t-form');
    if (!form) return;

    const tcart = (window as any).tcart;
    if (!tcart || !widgetInstance) return;

    const state = (widgetInstance as any).state;

    // Validate: delivery option must be selected
    if (!state?.selectedOption) {
      e.preventDefault();
      e.stopImmediatePropagation();
      alert('Пожалуйста, выберите способ доставки');
      return;
    }

    // Validate: courier address fields must be filled
    if (state.selectedOption.type === 'courier') {
      const addr = state.courierAddress;
      if (!addr?.street || !addr?.house) {
        e.preventDefault();
        e.stopImmediatePropagation();
        // Highlight empty fields
        const widget = (widgetInstance as any).container as HTMLElement;
        if (widget) {
          const streetInput = widget.querySelector('input[name="street"]') as HTMLInputElement;
          const houseInput = widget.querySelector('input[name="house"]') as HTMLInputElement;
          if (streetInput && !addr?.street) streetInput.style.borderColor = '#e53935';
          if (houseInput && !addr?.house) houseInput.style.borderColor = '#e53935';
          // Scroll to address form
          const addrContainer = widget.querySelector('.gw-delivery__address-container');
          addrContainer?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
    }

    const deliveryPrice = state.selectedOption.is_free ? 0 : state.selectedOption.price;

    // Set tcart.delivery in the format Tilda expects (see tcart__processDelivery)
    tcart.delivery = {
      name: state.selectedOption.name,
      price: deliveryPrice,
    };

    console.log('[GW Delivery] Set tcart.delivery for form submit:', tcart.delivery);
  }, true); // capture phase

  // Also hook form submit event as backup
  document.addEventListener('submit', (e) => {
    const form = e.target as HTMLFormElement;
    if (!form?.closest('.t706')) return;

    const tcart = (window as any).tcart;
    if (!tcart || !widgetInstance) return;

    const state = (widgetInstance as any).state;
    if (!state?.selectedOption) return;

    const deliveryPrice = state.selectedOption.is_free ? 0 : state.selectedOption.price;

    if (!tcart.delivery) {
      tcart.delivery = {
        name: state.selectedOption.name,
        price: deliveryPrice,
      };
      console.log('[GW Delivery] Set tcart.delivery on form submit:', tcart.delivery);
    }
  }, true);

  console.log('[GW Delivery] Delivery-for-submit handler installed');
}

/**
 * Hook Tilda's native cart functions to detect quantity changes and product additions
 * This is needed because Tilda doesn't always make fetch requests for cart updates
 */
function hookTildaCartFunctions(): void {
  const checkAndHook = () => {
    const win = window as any;

    // Hook tcart__addProduct - triggers when user adds a product to cart
    // Important: if checkout form is open, we need to recalculate delivery
    if (typeof win.tcart__addProduct === 'function' && !win.__gw_hooked_addProduct) {
      const originalAddProduct = win.tcart__addProduct;
      win.tcart__addProduct = function(...args: any[]) {
        const result = originalAddProduct.apply(this, args);
        console.log('[GW Delivery] tcart__addProduct called');
        // Use longer delay - Tilda needs time to update internal state
        debouncedRefreshFromTcart(500);
        return result;
      };
      win.__gw_hooked_addProduct = true;
      console.log('[GW Delivery] Hooked tcart__addProduct');
    }

    // Hook tcart__product__plus
    if (typeof win.tcart__product__plus === 'function' && !win.__gw_hooked_plus) {
      const originalPlus = win.tcart__product__plus;
      win.tcart__product__plus = function(e: Event) {
        const result = originalPlus.call(this, e);
        console.log('[GW Delivery] tcart__product__plus called');
        debouncedRefreshFromTcart();
        return result;
      };
      win.__gw_hooked_plus = true;
      console.log('[GW Delivery] Hooked tcart__product__plus');
    }

    // Hook tcart__product__minus
    if (typeof win.tcart__product__minus === 'function' && !win.__gw_hooked_minus) {
      const originalMinus = win.tcart__product__minus;
      win.tcart__product__minus = function(e: Event) {
        const result = originalMinus.call(this, e);
        console.log('[GW Delivery] tcart__product__minus called');
        debouncedRefreshFromTcart();
        return result;
      };
      win.__gw_hooked_minus = true;
      console.log('[GW Delivery] Hooked tcart__product__minus');
    }

    // Hook tcart__reDrawTotal for total synchronization
    if (typeof win.tcart__reDrawTotal === 'function' && !win.__gw_hooked_redraw) {
      const originalRedraw = win.tcart__reDrawTotal;
      win.tcart__reDrawTotal = function() {
        const result = originalRedraw.call(this);
        console.log('[GW Delivery] tcart__reDrawTotal called');
        // Sync our delivery total after Tilda redraws
        syncDeliveryTotal();
        return result;
      };
      win.__gw_hooked_redraw = true;
      console.log('[GW Delivery] Hooked tcart__reDrawTotal');
    }

    // Hook tcart__initDelivery - triggers when checkout form initializes delivery
    if (typeof win.tcart__initDelivery === 'function' && !win.__gw_hooked_initDelivery) {
      const originalInitDelivery = win.tcart__initDelivery;
      win.tcart__initDelivery = function(...args: any[]) {
        console.log('[GW Delivery] tcart__initDelivery called - Tilda initializing delivery');
        const result = originalInitDelivery.apply(this, args);
        // Our widget should already be injected via MutationObserver,
        // but this gives us a signal that delivery block is active
        return result;
      };
      win.__gw_hooked_initDelivery = true;
      console.log('[GW Delivery] Hooked tcart__initDelivery');
    }

    // Keep checking if critical functions aren't available yet
    const criticalHooks = ['__gw_hooked_plus', '__gw_hooked_minus', '__gw_hooked_addProduct'];
    const missingHooks = criticalHooks.filter(h => !win[h]);
    if (missingHooks.length > 0) {
      setTimeout(checkAndHook, 500);
    }
  };

  checkAndHook();
}

/**
 * Debounced refresh from tcart changes
 * @param delay - debounce delay in ms (default 300ms, use longer for addProduct)
 */
function debouncedRefreshFromTcart(delay: number = 300): void {
  if (refreshFromTcartTimeout) {
    clearTimeout(refreshFromTcartTimeout);
  }

  refreshFromTcartTimeout = setTimeout(() => {
    console.log('[GW Delivery] Refreshing from tcart change...');

    // Update cached cart data from tcart
    updateCachedCartFromTcart();

    // Refresh widget if it exists
    // Note: Don't check visibility - widget may be in popup with special CSS
    if (widgetInstance) {
      const widgetEl = document.getElementById('gw-delivery-widget');
      if (widgetEl) {
        console.log('[GW Delivery] Calling widgetInstance.refresh()');
        widgetInstance.refresh();
      } else {
        console.log('[GW Delivery] Widget element not found in DOM');
      }
    } else {
      console.log('[GW Delivery] No widget instance');
    }
  }, delay);
}

/**
 * Update cached cart data from window.tcart
 * CRITICAL: Also reads quantity directly from DOM inputs as Tilda may be async
 */
function updateCachedCartFromTcart(): void {
  const tcart = (window as any).tcart;
  if (!tcart?.products) return;

  // Build a map of quantities from DOM (most up-to-date source!)
  // CRITICAL: Tilda uses SPAN (.t706__product-quantity) for quantity, NOT input!
  const domQuantities: Record<string, number> = {};
  document.querySelectorAll('.t706__cartwin-products-i, .t706__product, .t-store__cart__item').forEach((el) => {
    const sku = el.getAttribute('data-product-sku') || '';
    // First try span (Tilda's actual element), then input as fallback
    const qtySpan = el.querySelector<HTMLElement>('.t706__product-quantity');
    const qtyInput = el.querySelector<HTMLInputElement>('.t706__product-quantity-inp, .t706__cartwin-prodamount-input');

    let qty = 1;
    if (qtySpan) {
      qty = parseInt(qtySpan.textContent || '1', 10) || 1;
    } else if (qtyInput) {
      qty = parseInt(qtyInput.value || '1', 10) || 1;
    }

    if (sku) {
      domQuantities[sku] = qty;
    }
  });

  cachedCartData = {
    cartAmount: parseFloat(tcart.amount) || 0,
    itemsCount: tcart.products.reduce((sum: number, p: any) =>
      sum + (parseInt(p.quantity, 10) || 1), 0),
    products: tcart.products.map((p: any) => {
      // Use DOM quantity if available (more up-to-date than tcart after manual input)
      const domQty = domQuantities[p.sku];
      const quantity = domQty !== undefined ? domQty : (parseInt(p.quantity, 10) || 1);
      return {
        sku: p.sku || '',
        name: p.name || '',
        price: parseFloat(p.price) || 0,
        quantity,
        weight: parseFloat(p.pack_m || '0'),  // grams
        length: Math.ceil(parseFloat(p.pack_x || '0') / 10) || 0,
        width: Math.ceil(parseFloat(p.pack_y || '0') / 10) || 0,
        height: Math.ceil(parseFloat(p.pack_z || '0') / 10) || 0,
      };
    }),
  };

  // Recalculate totals based on actual quantities
  const recalculatedTotal = cachedCartData.products.reduce((sum, p) =>
    sum + (p.price * p.quantity), 0);

  // Update global exposed data with recalculated total
  const cartData = getCartData();
  (window as any).__gwCachedCartData = {
    products: cartData.items,
    totalPrice: recalculatedTotal, // Use recalculated total!
    totalWeight: cartData.totalWeight,
    dimensions: cartData.dimensions,
  };

  console.log('[GW Delivery] Updated cache from tcart:', {
    items: cachedCartData.products.length,
    amount: cachedCartData.cartAmount,
    recalculatedTotal,
    domQuantities,
  });
}

/**
 * Sync delivery total after Tilda redraws
 * This is called after tcart__reDrawTotal() to restore our delivery selection
 * because Tilda may have overwritten it with its own values
 */
function syncDeliveryTotal(): void {
  const win = window as any;
  const tcart = win.tcart;
  if (!tcart) return;

  // Prevent recursive calls
  if (win.__gwSyncingDelivery) return;
  win.__gwSyncingDelivery = true;

  try {
    const state = widgetInstance ? (widgetInstance as any).state : null;
    const hasSelection = state?.selectedOption != null;
    const deliveryPrice = hasSelection
      ? (state.selectedOption.is_free ? 0 : state.selectedOption.price)
      : 0;

    // Update tcart.amount
    const prodAmount = parseFloat(tcart.prodamount) || 0;
    tcart.amount = prodAmount + deliveryPrice;
    tcart.__gwDeliveryPrice = deliveryPrice;

    if (widgetInstance && hasSelection) {
      // Re-apply our price breakdown after Tilda redraws
      [50, 150, 300].forEach(delay => {
        setTimeout(() => {
          (widgetInstance as any).updateTildaTotal();
        }, delay);
      });
    } else if (widgetInstance) {
      // No selection — hide stale rows that Tilda may have re-created
      [50, 150, 300].forEach(delay => {
        setTimeout(() => {
          (widgetInstance as any).hideStaleDeliveryRows();
        }, delay);
      });
    }
  } finally {
    setTimeout(() => {
      win.__gwSyncingDelivery = false;
    }, 400);
  }
}

/**
 * Setup MutationObserver to watch for Vienna text and replace it
 * This is needed because Tilda may redraw the DOM after our updates
 */
function setupDeliveryTextObserver(): void {
  // Disconnect existing observer if any
  if (deliveryTextObserver) {
    deliveryTextObserver.disconnect();
  }

  const win = window as any;
  const tcart = win.tcart;

  deliveryTextObserver = new MutationObserver((mutations) => {
    // Check if we have saved city info
    const savedCity = tcart?.__gwCity;
    if (!savedCity) return;

    // Debounce the check
    if (win.__gwCheckingVienna) return;
    win.__gwCheckingVienna = true;

    setTimeout(() => {
      // Check for Vienna text in the cart area
      const cartContainer = document.querySelector('.t706__cartwin, .t706__orderform');
      if (cartContainer) {
        const text = cartContainer.textContent || '';
        if (text.includes('Vienna') || text.includes('Austria')) {
          console.log('[GW Delivery] MutationObserver detected Vienna text, replacing...');
          replaceViennaText(savedCity);
        }
      }
      win.__gwCheckingVienna = false;
    }, 10);
  });

  // Start observing the document body for changes
  deliveryTextObserver.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  console.log('[GW Delivery] Delivery text observer started');
}

/**
 * Replace Vienna text with actual city
 */
function replaceViennaText(city: { name: string; country_code?: string }): void {
  const cityName = city.name || '';
  const countryName = city.country_code === 'RU' ? 'Россия' :
                     city.country_code === 'BY' ? 'Беларусь' :
                     city.country_code === 'KZ' ? 'Казахстан' : '';
  const locationText = countryName ? `${cityName} / ${countryName}` : cityName;

  // Use TreeWalker to find all text nodes containing Vienna
  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_TEXT,
    null
  );

  const nodesToUpdate: Text[] = [];
  let node: Text | null;
  while (node = walker.nextNode() as Text) {
    const text = node.textContent || '';
    if (text.includes('Vienna') || text.includes('Austria')) {
      nodesToUpdate.push(node);
    }
  }

  // Replace text in all found nodes
  nodesToUpdate.forEach(textNode => {
    if (textNode.parentElement) {
      // Replace Vienna/Austria with our city
      let newText = textNode.textContent || '';
      newText = newText.replace(/Vienna\s*\/?\s*Austria/gi, locationText);
      newText = newText.replace(/Vienna/gi, cityName);
      newText = newText.replace(/Austria/gi, countryName || '');
      textNode.textContent = newText;
      console.log('[GW Delivery] Replaced Vienna text with:', locationText);
    }
  });
}

/**
 * Setup fallback listeners for cart quantity changes
 */
function setupCartQuantityListeners(): void {
  // Selectors for quantity inputs in Tilda cart
  const quantityInputSelectors = [
    '.t706__product-quantity-inp',        // ACTUAL Tilda quantity input!
    '.t706__cartwin-prodamount-input',
    '.t-store__cart__item-amount input',
    'input[name*="quantity"]',
    'input[type="number"]',
  ].join(', ');

  // Listen to 'input' event for real-time updates while typing
  // We DON'T trigger API call here - just show skeleton
  document.addEventListener('input', (e) => {
    const target = e.target as HTMLElement;
    if (target.matches(quantityInputSelectors)) {
      console.log('[GW Delivery] Quantity input typing');
      // Show skeleton for smooth transition (but don't recalculate yet)
      if (widgetInstance) {
        (widgetInstance as any).showPriceBreakdownSkeleton();
      }
      // DON'T call debouncedRefreshFromTcart here - wait for blur/change
      // because Tilda hasn't updated tcart yet while user is still typing
    }
  }, true);

  // Listen to 'change' event (fires on blur after value change)
  // This is the main trigger for manual input - Tilda has updated tcart by now
  document.addEventListener('change', (e) => {
    const target = e.target as HTMLElement;
    if (target.matches(quantityInputSelectors)) {
      console.log('[GW Delivery] Quantity input changed (blur)');
      // Show skeleton for smooth transition
      if (widgetInstance) {
        (widgetInstance as any).showPriceBreakdownSkeleton();
      }
      // Use longer delay to ensure Tilda has fully processed the change
      // Tilda updates tcart asynchronously after input blur
      setTimeout(() => {
        debouncedRefreshFromTcart(100); // shorter debounce since we already waited
      }, 500);
    }
  }, true);

  // Listen to 'blur' event as fallback (user finished editing)
  document.addEventListener('blur', (e) => {
    const target = e.target as HTMLElement;
    if (target.matches(quantityInputSelectors)) {
      console.log('[GW Delivery] Quantity input blur');
      // Delay to let Tilda process the change first
      setTimeout(() => {
        if (widgetInstance) {
          (widgetInstance as any).showPriceBreakdownSkeleton();
        }
        debouncedRefreshFromTcart(100);
      }, 500);
    }
  }, true);

  // Listen to click on +/- buttons
  // CRITICAL: Tilda uses .t706__product-plus and .t706__product-minus classes!
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.matches('.t706__product-plus, .t706__product-minus, .t706__cartwin-prodamount-btn, [data-quantity-plus], [data-quantity-minus]')) {
      console.log('[GW Delivery] Quantity +/- button clicked');
      // Show skeleton immediately for smooth transition
      if (widgetInstance) {
        (widgetInstance as any).showPriceBreakdownSkeleton();
      }
      // Use longer delay to let Tilda update first
      setTimeout(() => debouncedRefreshFromTcart(), 500);
    }
  }, true);

  // Watch for changes in quantity spans (Tilda updates .t706__product-quantity text)
  const cartContainer = document.querySelector('.t706__cartwin, .t706__sidebar, .t-store__cart');
  if (cartContainer) {
    const qtyObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const target = mutation.target as HTMLElement;
        // Check if this is a quantity element or its parent
        if (target.classList?.contains('t706__product-quantity') ||
            target.closest?.('.t706__product-quantity') ||
            target.closest?.('.t706__product-plusminus')) {
          console.log('[GW Delivery] Quantity element changed via MutationObserver');
          if (widgetInstance) {
            (widgetInstance as any).showPriceBreakdownSkeleton();
          }
          setTimeout(() => debouncedRefreshFromTcart(), 500);
          break;
        }
      }
    });
    qtyObserver.observe(cartContainer, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    console.log('[GW Delivery] MutationObserver set up for quantity changes');
  }

  // Listen to keydown for Enter key in quantity input
  document.addEventListener('keydown', (e) => {
    const target = e.target as HTMLElement;
    if (target.matches(quantityInputSelectors) && (e as KeyboardEvent).key === 'Enter') {
      console.log('[GW Delivery] Quantity input Enter pressed');
      // Prevent form submission, trigger recalculation
      e.preventDefault();
      if (widgetInstance) {
        (widgetInstance as any).showPriceBreakdownSkeleton();
      }
      // Delay to let Tilda process first
      setTimeout(() => debouncedRefreshFromTcart(), 100);
    }
  }, true);
}

/**
 * Setup the injector that watches for cart/checkout appearance
 */
function setupInjector(config: WidgetConfig): void {
  // Use MutationObserver to watch for cart popup
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement) {
          // Check if cart or checkout appeared
          if (
            node.matches(SELECTORS.CART_POPUP) ||
            node.querySelector(SELECTORS.CART_FORM) ||
            node.querySelector(SELECTORS.CHECKOUT_FORM)
          ) {
            setTimeout(() => injectWidget(config), 100);
          }
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // Also listen for Tilda-specific events
  listenTildaEvents(config);

  // Check if already open
  checkAndInject(config);

  if (config.debug) {
    console.log('[GW Delivery] Injector setup complete');
  }
}

/**
 * Listen to Tilda cart events
 */
function listenTildaEvents(config: WidgetConfig): void {
  // Listen for cart shown event
  document.addEventListener('greenwaxCartShowed', () => {
    setTimeout(() => injectWidget(config), 200);
  });

  // Listen for Tilda cart events
  const eventNames = [
    't706:cartshown',
    't-store:cartshow',
    'renderDeliveryServices',
  ];

  eventNames.forEach(eventName => {
    document.addEventListener(eventName, () => {
      setTimeout(() => injectWidget(config), 200);
    });
  });

  // Intercept checkout button clicks
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (
      target.matches('.t706__cartwin-checkout-btn') ||
      target.matches('[data-elem-id="checkout"]') ||
      target.matches('.t-store__cart__checkout-btn')
    ) {
      setTimeout(() => injectWidget(config), 300);
    }
  });
}

/**
 * Check if cart/checkout is already open and inject
 */
function checkAndInject(config: WidgetConfig): void {
  const existingCart = document.querySelector(SELECTORS.CART_POPUP);
  const existingCheckout = document.querySelector(SELECTORS.CHECKOUT_FORM);

  if (existingCart || existingCheckout) {
    setTimeout(() => injectWidget(config), 100);
  }
}

/**
 * Inject widget into cart/checkout
 */
function injectWidget(config: WidgetConfig): void {
  // Check if already injected
  if (document.getElementById(SELECTORS.WIDGET_ID)) {
    if (config.debug) {
      console.log('[GW Delivery] Widget already injected');
    }
    return;
  }

  // Find original delivery block
  const originalDelivery = document.querySelector(SELECTORS.DELIVERY_ORIGINAL);

  // Find form to inject into (fallback locations)
  const form = document.querySelector(SELECTORS.CART_FORM) ||
               document.querySelector(SELECTORS.CHECKOUT_FORM);

  if (!originalDelivery && !form) {
    if (config.debug) {
      console.log('[GW Delivery] No suitable injection point found');
    }
    return;
  }

  // Hide original delivery block
  if (originalDelivery) {
    (originalDelivery as HTMLElement).style.display = 'none';
  }

  // Create widget container
  const container = document.createElement('div');
  container.id = SELECTORS.WIDGET_ID;

  // Insert widget AFTER the phone field (Телефон)
  // Priority: 1) After phone field, 2) After original delivery, 3) After first field
  const phoneField = form?.querySelector('.t-input-group_ph');

  if (phoneField) {
    // Insert after the phone field
    phoneField.insertAdjacentElement('afterend', container);
    console.log('[GW Delivery] Inserted widget after phone field');
  } else if (originalDelivery) {
    originalDelivery.insertAdjacentElement('afterend', container);
    console.log('[GW Delivery] Inserted widget after original delivery block');
  } else if (form) {
    // Fallback: find a good spot in the form
    const firstField = form.querySelector('.t-input-group, .t706__form-inputbox');
    if (firstField) {
      firstField.insertAdjacentElement('afterend', container);
    } else {
      form.insertAdjacentElement('afterbegin', container);
    }
    console.log('[GW Delivery] Inserted widget as fallback position');
  }

  // Create and initialize widget
  widgetInstance = new DeliveryWidget(container, config);

  // Get cart data and initialize
  const cartData = getCartData();
  widgetInstance.init(cartData);

  // Setup observer to watch for Vienna text replacement
  setupDeliveryTextObserver();

  if (config.debug) {
    console.log('[GW Delivery] Widget injected successfully');
  }
}

/**
 * Normalize weight to grams with sanity checks
 * Tilda pack_m is in GRAMS (not kg!)
 */
function normalizeWeight(rawWeight: number): number {
  if (!rawWeight || rawWeight <= 0) {
    return 100; // default 100g
  }

  // Sanity check: cosmetics/candles typically weigh < 5kg per item
  // If weight > 50kg, something is likely wrong
  const MAX_REASONABLE_WEIGHT = 50000; // 50kg in grams
  if (rawWeight > MAX_REASONABLE_WEIGHT) {
    console.warn(`[GW Delivery] Suspicious weight ${rawWeight}g, capping at 50kg`);
    return MAX_REASONABLE_WEIGHT;
  }

  return rawWeight;
}

/**
 * Calculate aggregate package dimensions from items
 * Returns dimensions in cm for CDEK API
 */
function calculateDimensions(
  items: Array<{ length: number; width: number; height: number; quantity: number }>,
  minDim: number
): { length: number; width: number; height: number } {
  if (!items.length) {
    return { length: minDim, width: minDim, height: minDim };
  }

  // Max length and width (items packed side by side)
  const maxLength = Math.max(...items.map(i => i.length || minDim), minDim);
  const maxWidth = Math.max(...items.map(i => i.width || minDim), minDim);

  // Sum heights (items stacked)
  const totalHeight = items.reduce(
    (sum, i) => sum + (i.height || minDim) * i.quantity,
    0
  );

  // Apply CDEK limits (max 120x80x80 cm for most tariffs)
  return {
    length: Math.max(minDim, Math.min(maxLength, 120)),
    width: Math.max(minDim, Math.min(maxWidth, 80)),
    height: Math.max(minDim, Math.min(totalHeight, 80)),
  };
}

/**
 * Get cart data from Tilda
 * Priority: 1) cached cart-api data, 2) window.tcart, 3) fallback defaults
 */
function getCartData() {
  // 1. Use cached cart-api data if available (most accurate)
  if (cachedCartData && cachedCartData.products.length > 0) {
    const items = cachedCartData.products.map(p => ({
      sku: p.sku,
      quantity: p.quantity,
      price: p.price,
      weight: normalizeWeight(p.weight),
      length: p.length,
      width: p.width,
      height: p.height,
    }));

    const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    let totalWeight = items.reduce((sum, item) => sum + item.weight * item.quantity, 0);

    // Cap total weight at 100kg max for shipping
    const MAX_TOTAL_WEIGHT = 100000; // 100kg in grams
    totalWeight = Math.min(totalWeight, MAX_TOTAL_WEIGHT);

    // Calculate aggregate dimensions
    const MIN_DIM = 10; // CDEK minimum 10cm
    const dimensions = calculateDimensions(items, MIN_DIM);

    console.log('[GW Delivery] Using cart-api data:', {
      items: items.length,
      totalPrice,
      totalWeight,
      dimensions,
    });

    return {
      items,
      totalPrice: totalPrice || cachedCartData.cartAmount,
      totalWeight: totalWeight > 0 ? totalWeight : 500,
      dimensions,
    };
  }

  // 2. Try Tilda API (window.tcart)
  const tildaCart = (window as any).tcart;
  if (tildaCart?.products) {
    const items = tildaCart.products.map((p: any) => {
      // pack_m in Tilda is already in GRAMS (NOT kg!)
      const rawWeight = parseFloat(p.pack_m || p.weight || 0);
      return {
        sku: p.sku || '',
        quantity: parseInt(p.quantity, 10) || 1,
        price: parseFloat(p.price) || 0,
        weight: normalizeWeight(rawWeight),
        // Dimensions in mm, convert to cm
        length: Math.ceil(parseFloat(p.pack_x || '0') / 10) || 0,
        width: Math.ceil(parseFloat(p.pack_y || '0') / 10) || 0,
        height: Math.ceil(parseFloat(p.pack_z || '0') / 10) || 0,
      };
    });

    const totalPrice = items.reduce((sum: number, item: any) => sum + item.price * item.quantity, 0);
    let totalWeight = items.reduce((sum: number, item: any) => sum + item.weight * item.quantity, 0);

    // Cap total weight at 100kg max
    const MAX_TOTAL_WEIGHT = 100000;
    totalWeight = Math.min(totalWeight, MAX_TOTAL_WEIGHT);

    // Calculate aggregate dimensions (max length/width, sum height)
    const MIN_DIM = 10; // CDEK minimum
    const dimensions = calculateDimensions(items, MIN_DIM);

    console.log('[GW Delivery] Using tcart data:', {
      items: items.length,
      totalPrice,
      totalWeight,
      dimensions,
    });

    return {
      items,
      totalPrice: totalPrice || 1000,
      totalWeight: totalWeight > 0 ? totalWeight : 500,
      dimensions,
    };
  }

  // 3. Fallback - reasonable defaults
  console.log('[GW Delivery] Using fallback cart data');
  return {
    items: [],
    totalPrice: 1000,
    totalWeight: 500,
    dimensions: { length: 20, width: 15, height: 15 }, // reasonable defaults for small package
  };
}

/**
 * Refresh the widget (call when cart changes)
 */
function refresh(): void {
  if (widgetInstance) {
    widgetInstance.refresh();
  }
}

/**
 * Get currently selected option
 */
function getSelectedOption(): DeliveryOption | null {
  // Dispatch event and wait for response
  return null; // TODO: implement getter
}

/**
 * Load Yandex Maps API dynamically
 * Returns a promise that resolves when API is ready
 */
function loadYandexMaps(): Promise<void> {
  return new Promise((resolve) => {
    // Already loaded
    if (yandexMapsLoaded && (window as any).ymaps) {
      resolve();
      return;
    }

    // Already loading - add callback
    if (yandexMapsLoading) {
      yandexMapsCallbacks.push(resolve);
      return;
    }

    // Start loading
    yandexMapsLoading = true;
    yandexMapsCallbacks.push(resolve);

    const script = document.createElement('script');
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${YANDEX_MAPS_API_KEY}&lang=ru_RU`;
    script.async = true;

    script.onload = () => {
      const ymaps = (window as any).ymaps;
      if (ymaps) {
        ymaps.ready(() => {
          yandexMapsLoaded = true;
          console.log('[GW Delivery] Yandex Maps API loaded');
          // Call all waiting callbacks
          yandexMapsCallbacks.forEach(cb => cb());
          yandexMapsCallbacks = [];
        });
      } else {
        console.error('[GW Delivery] Yandex Maps API script loaded but ymaps is undefined');
        yandexMapsCallbacks.forEach(cb => cb());
        yandexMapsCallbacks = [];
      }
    };

    script.onerror = () => {
      console.error('[GW Delivery] Failed to load Yandex Maps API');
      yandexMapsLoading = false;
      yandexMapsCallbacks.forEach(cb => cb());
      yandexMapsCallbacks = [];
    };

    document.head.appendChild(script);
    console.log('[GW Delivery] Loading Yandex Maps API...');
  });
}


/**
 * Intercept Tilda form submit to capture delivery data
 * Hooks into multiple Tilda submission patterns:
 *   1. tcart form submit events
 *   2. .t-form submit events
 *   3. Tilda-specific submit button clicks
 *   4. Fetch interception for payment/order requests
 */
function setupFormSubmitInterceptor(): void {
  // Reset submission flag on page load
  orderSubmitted = false;

  // 1. Hook Tilda cart form submit
  document.addEventListener('submit', (e) => {
    const form = e.target as HTMLFormElement;
    if (!form) return;

    // Check if this is a Tilda cart/checkout form
    const isTildaForm =
      form.classList.contains('t706__form') ||
      form.classList.contains('t-form') ||
      form.closest('.t706__cartwin') !== null ||
      form.closest('.t706__orderform') !== null ||
      form.querySelector('input[name="tildaspec-formid"]') !== null;

    if (isTildaForm) {
      console.log('[GW Delivery] Tilda form submit intercepted');
      // Collect and send order data (non-blocking, do NOT prevent form submit)
      collectAndSubmitOrder();
    }
  }, true); // Use capture phase to run before Tilda

  // 2. Hook Tilda submit button clicks (backup)
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const isSubmitBtn =
      target.matches('.t706__submit, .t706__cartwin-checkout-btn, .t-submit') ||
      target.closest('.t706__submit') !== null ||
      target.closest('.t-submit') !== null ||
      (target.tagName === 'BUTTON' && target.closest('.t706__form'));

    if (isSubmitBtn) {
      console.log('[GW Delivery] Tilda submit button clicked');
      // Short delay to let form validation happen first
      setTimeout(() => collectAndSubmitOrder(), 50);
    }
  }, true);

  // 3. Hook fetch for Tilda payment/order API calls
  const originalFetch = window.fetch;
  // Only wrap if not already wrapped by our cart interceptor
  // The cart interceptor is already set up, but we need to also watch for order submissions
  const currentFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await currentFetch.apply(this, args);
    const url = args[0]?.toString() || '';

    // Check if this is a Tilda order/payment request
    if (
      url.includes('/order/') ||
      url.includes('payment') ||
      url.includes('submitorder') ||
      url.includes('tilda.ws') && (args[1] as any)?.method === 'POST'
    ) {
      console.log('[GW Delivery] Tilda order/payment request detected:', url);
      collectAndSubmitOrder();
    }

    return response;
  };

  // 4. Listen for Tilda custom events related to order submission
  document.addEventListener('tildaform:aftersubmit', () => {
    console.log('[GW Delivery] tildaform:aftersubmit event');
    collectAndSubmitOrder();
  });
  document.addEventListener('tildaform:aftersuccess', () => {
    console.log('[GW Delivery] tildaform:aftersuccess event');
    collectAndSubmitOrder();
  });

  console.log('[GW Delivery] Form submit interceptor installed');
}

/**
 * Collect order data from widget and submit to backend
 * Non-blocking: does not interfere with Tilda's own form submission
 */
function collectAndSubmitOrder(): void {
  // Prevent double submission
  if (orderSubmitted) {
    console.log('[GW Delivery] Order already submitted, skipping');
    return;
  }

  if (!widgetInstance || !apiClient) {
    console.warn('[GW Delivery] Widget or API client not available for order submission');
    return;
  }

  // Collect payload from widget
  const payload = (widgetInstance as any).collectOrderPayload() as DeliveryOrderPayload | null;
  if (!payload) {
    console.warn('[GW Delivery] No delivery data to submit (no option selected?)');
    return;
  }

  // Enrich with customer data from Tilda form (backup, widget also reads it)
  if (!payload.customer) {
    payload.customer = getCustomerDataFromTilda();
  }

  // Try to get Tilda order ID from tcart or form
  if (!payload.orderId) {
    const tcart = (window as any).tcart;
    if (tcart?.orderId) {
      payload.orderId = String(tcart.orderId);
    } else if (tcart?.orderid) {
      payload.orderId = String(tcart.orderid);
    }
    // Also check hidden form field
    if (!payload.orderId) {
      const orderInput = document.querySelector<HTMLInputElement>('input[name="order_id"], input[name="orderid"]');
      if (orderInput && orderInput.value) {
        payload.orderId = orderInput.value;
      }
    }
  }

  orderSubmitted = true;

  console.log('[GW Delivery] Submitting delivery order:', payload);

  // Submit asynchronously (fire-and-forget, do not block Tilda)
  apiClient.submitDeliveryOrder(payload)
    .then(result => {
      if (result.success) {
        console.log('[GW Delivery] Order submitted to backend:', result.orderId);
        // Clear buffer after successful submission
        DeliveryWidget.clearBuffer();
      } else {
        console.warn('[GW Delivery] Order submission failed (saved to localStorage):', result.error);
      }
    })
    .catch(err => {
      console.error('[GW Delivery] Order submission error:', err);
    });

  // Reset after 30 seconds to allow resubmission if user goes back
  setTimeout(() => {
    orderSubmitted = false;
  }, 30000);
}

/**
 * Extract customer data directly from Tilda form fields
 * Backup method used by widget.ts if DeliveryWidget didn't capture it
 */
function getCustomerDataFromTilda(): { name?: string; phone?: string; email?: string } | undefined {
  const result: { name?: string; phone?: string; email?: string } = {};
  let found = false;

  // Name
  const nameSelectors = [
    'input[name="Name"]',
    'input[name="name"]',
    '.t-input-group_nm input',
  ];
  for (const sel of nameSelectors) {
    const input = document.querySelector<HTMLInputElement>(sel);
    if (input && input.value.trim()) {
      result.name = input.value.trim();
      found = true;
      break;
    }
  }

  // Phone
  const phoneSelectors = [
    'input[name="Phone"]',
    'input[name="phone"]',
    '.t-input-group_ph input[type="tel"]',
    '.t-input-group_ph input',
  ];
  for (const sel of phoneSelectors) {
    const input = document.querySelector<HTMLInputElement>(sel);
    if (input && input.value.trim()) {
      result.phone = input.value.trim();
      found = true;
      break;
    }
  }

  // Email
  const emailSelectors = [
    'input[name="Email"]',
    'input[name="email"]',
    'input[type="email"]',
    '.t-input-group_em input',
  ];
  for (const sel of emailSelectors) {
    const input = document.querySelector<HTMLInputElement>(sel);
    if (input && input.value.trim()) {
      result.email = input.value.trim();
      found = true;
      break;
    }
  }

  return found ? result : undefined;
}

// Export global API
const GWDelivery = {
  init,
  refresh,
  getSelectedOption,
  loadYandexMaps,
  collectAndSubmitOrder,
  version: '1.0.0',
};

// Expose to window
(window as any).GWDelivery = GWDelivery;

export default GWDelivery;
